import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { legalApi, faqApi } from "../../services/api";
import { PageHeader, Pagination } from "../../components/ui";
import { showToast } from "../../utils/toast";
import {
  AlertTriangle,
  Trash2,
  Save,
  Plus,
  Eye,
  Pencil,
  ChevronDown,
  Globe,
} from "lucide-react";

/* ══════════════════════════════════════
   QUILL LOADER (singleton)
══════════════════════════════════════ */
let quillLoaded = false;
let quillLoadCallbacks = [];

function loadQuill(cb) {
  if (window.Quill) { cb(); return; }
  if (quillLoaded) { quillLoadCallbacks.push(cb); return; }
  quillLoaded = true;

  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href = "https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.min.css";
  document.head.appendChild(link);

  const script = document.createElement("script");
  script.src = "https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.min.js";
  script.onload = () => {
    cb();
    quillLoadCallbacks.forEach(f => f());
    quillLoadCallbacks = [];
  };
  document.head.appendChild(script);
}

/* ══════════════════════════════════════
   CONFIRM DIALOG
══════════════════════════════════════ */
function ConfirmDialog({ dialog, onClose }) {
  if (!dialog) return null;
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(0,0,0,0.4)",
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, padding: "28px 32px",
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          minWidth: 320, maxWidth: 400,
          display: "flex", flexDirection: "column", gap: 12,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 14, color: "#111", display: "flex", alignItems: "center", gap: 8 }}>
          <AlertTriangle size={18} strokeWidth={2.5} color="#dc2626" style={{ flexShrink: 0 }} />
          {dialog.message}
        </div>
        <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>
          {dialog.subText}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
          <button
            onClick={async () => { onClose(); await dialog.onConfirm(); }}
            style={{
              padding: "8px 20px", borderRadius: 7, border: "none",
              background: "#dc2626", color: "#fff",
              fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}
          >
            Confirm
          </button>
          <button
            onClick={onClose}
            style={{
              padding: "8px 20px", borderRadius: 7,
              border: "1px solid #ddd", background: "#f5f5f5",
              fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#333",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   RICH TEXT EDITOR
══════════════════════════════════════ */
function RichEditor({ value, onChange, editorRef }) {
  const containerRef = useRef(null);
  const quillRef     = useRef(null);
  const [ready, setReady] = useState(!!window.Quill);

  useEffect(() => { loadQuill(() => setReady(true)); }, []);

  useEffect(() => {
    if (!ready || !containerRef.current || quillRef.current) return;

    const q = new window.Quill(containerRef.current, {
      theme: "snow",
      placeholder: "Start writing your document here…",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["link"],
          ["clean"],
        ],
      },
    });

    if (value) {
      try { q.setContents(JSON.parse(value)); }
      catch { q.clipboard.dangerouslyPasteHTML(value || ""); }
    }

    q.on("text-change", () => {
      const html  = containerRef.current.querySelector(".ql-editor")?.innerHTML || "";
      const delta = JSON.stringify(q.getContents());
      onChange({ html, delta });
    });

    quillRef.current = q;
    if (editorRef) editorRef.current = q;
  }, [ready]);

  return (
    <div>
      <style>{`
        .ql-container { font-family: var(--font-body); font-size: 14px; min-height: 400px; }
        .ql-editor    { min-height: 400px; color: var(--text-primary); }
        .ql-toolbar   { border-color: var(--border) !important; background: rgba(15,61,62,0.02); border-radius: 10px 10px 0 0; }
        .ql-container { border-color: var(--border) !important; border-radius: 0 0 10px 10px; background: var(--bg-card); }
        .ql-editor h1,h2,h3 { font-family: var(--font-display); color: #0F3D3E; }
        .ql-editor h1 { font-size: 22px; } .ql-editor h2 { font-size: 18px; } .ql-editor h3 { font-size: 15px; }
        .ql-editor a  { color: #0F3D3E; }
        .ql-editor p  { margin-bottom: 8px; line-height: 1.6; }
        .ql-editor ol, .ql-editor ul { padding-left: 20px; }
        .ql-snow .ql-toolbar button:hover, .ql-snow .ql-toolbar button.ql-active { color: #0F3D3E !important; }
        .ql-snow .ql-toolbar button:hover .ql-stroke, .ql-snow .ql-toolbar button.ql-active .ql-stroke { stroke: #0F3D3E !important; }
      `}</style>
      {!ready ? (
        <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontSize: 13 }}>
          Loading editor…
        </div>
      ) : (
        <div ref={containerRef} />
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   PREVIEW PANE
══════════════════════════════════════ */
function Preview({ html }) {
  return (
    <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 12, padding: "28px 32px", boxShadow: "var(--shadow-sm)" }}>
      <style>{`
        .legal-preview h1 { font-family: var(--font-display); font-size: 22px; color: #0F3D3E; margin-bottom: 16px; }
        .legal-preview h2 { font-family: var(--font-display); font-size: 17px; color: #0F3D3E; margin: 20px 0 10px; }
        .legal-preview h3 { font-size: 15px; font-weight: 700; color: var(--text-primary); margin: 16px 0 8px; }
        .legal-preview p  { color: var(--text-secondary); line-height: 1.7; margin-bottom: 12px; font-size: 14px; }
        .legal-preview ul,
        .legal-preview ol { color: var(--text-secondary); line-height: 1.7; margin-bottom: 12px; font-size: 14px; padding-left: 24px; }
        .legal-preview li { margin-bottom: 6px; }
        .legal-preview strong { color: var(--text-primary); }
        .legal-preview a  { color: #0F3D3E; }
      `}</style>
      <div
        className="legal-preview"
        dangerouslySetInnerHTML={{ __html: html || "<p style='color:var(--text-muted)'>Nothing to preview yet…</p>" }}
      />
    </div>
  );
}

/* ══════════════════════════════════════
   SHARED SMALL COMPONENTS
══════════════════════════════════════ */
function StyledInput({ value, onChange, placeholder, style = {} }) {
  return (
    <input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: "100%", padding: "9px 12px", fontSize: 13,
        border: "1px solid var(--border)", borderRadius: 8, outline: "none",
        color: "var(--text-primary)", background: "var(--bg-card)",
        fontFamily: "var(--font-body)", boxSizing: "border-box", ...style,
      }}
      onFocus={e => e.target.style.borderColor = "#0F3D3E"}
      onBlur={e  => e.target.style.borderColor = "var(--border)"}
    />
  );
}

function ActionButton({ saving, onClick, label = "Save Document", small = false, disabled = false }) {
  const isDisabled = saving || disabled;
  const Icon = label.includes("Add") ? Plus : Save;
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      style={{
        padding: small ? "7px 14px" : "9px 22px",
        borderRadius: 8, border: "none",
        cursor: isDisabled ? "not-allowed" : "pointer",
        background: "#0F3D3E", color: "#fff",
        fontSize: small ? 12 : 13, fontWeight: 700,
        fontFamily: "var(--font-body)",
        opacity: isDisabled ? 0.6 : 1,
        display: "flex", alignItems: "center", gap: 6,
        transition: "filter 0.15s",
      }}
      onMouseEnter={e => !isDisabled && (e.currentTarget.style.filter = "brightness(1.1)")}
      onMouseLeave={e => (e.currentTarget.style.filter = "none")}
    >
      {saving
        ? "Saving…"
        : <><Icon size={small ? 12 : 13} strokeWidth={2.5} /> {label}</>
      }
    </button>
  );
}

function SectionHeader({ title }) {
  return (
    <div style={{
      padding: "14px 20px", borderBottom: "1px solid var(--border)",
      background: "rgba(15,61,62,0.03)", display: "flex", alignItems: "center", gap: 10,
    }}>
      <div style={{ width: 4, height: 18, borderRadius: 2, background: "#0F3D3E", flexShrink: 0 }} />
      <h3 style={{ fontFamily: "var(--font-display)", fontSize: 14, color: "#0F3D3E", fontWeight: 600 }}>{title}</h3>
    </div>
  );
}

/* ══════════════════════════════════════
   DOC EDITOR
══════════════════════════════════════ */
const DOC_TYPES = {
  "/legal/terms-and-conditions": { key: "TERMS_AND_CONDITIONS", label: "Terms & Conditions" },
  "/legal/privacy-policy":       { key: "PRIVACY_POLICY",       label: "Privacy Policy"     },
};

function DocEditor() {
  const location = useLocation();
  const docType  = DOC_TYPES[location.pathname];

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode,    setMode]    = useState("edit");
  const [content, setContent] = useState("");
  const [delta,   setDelta]   = useState("");
  const [title,   setTitle]   = useState(docType?.label || "");
  const [saving,  setSaving]  = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (!docType) return;
    setLoading(true);
    setContent(""); setDelta(""); setMode("edit");
    legalApi.getByType(docType.key)
      .then(r => {
        const doc = r.data.data;
        if (doc) {
          setData(doc);
          setTitle(doc.title || docType.label);
          setContent(doc.content || "");
          setDelta(doc.contentDelta || "");
        } else {
          setTitle(docType.label);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [location.pathname]);

  const handleChange = ({ html, delta: d }) => { setContent(html); setDelta(d); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await legalApi.upsert({ type: docType.key, title, content, contentDelta: delta });
      setData(res.data.data);
      showToast(`${docType.label} saved`);
    } catch {
      showToast("Failed to save", "error");
    }
    setSaving(false);
  };

  if (!docType) return null;
  if (loading)  return <div style={{ padding: 60, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>Loading…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {["edit", "preview"].map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                padding: "6px 16px", borderRadius: 20, border: "1px solid var(--border)",
                cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "var(--font-body)",
                background: mode === m ? "#0F3D3E" : "transparent",
                color:      mode === m ? "#fff"    : "var(--text-secondary)",
                transition: "all 0.15s",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              {m === "edit"
                ? <><Pencil size={11} strokeWidth={2.5} /> Edit</>
                : <><Eye    size={11} strokeWidth={2.5} /> Preview</>
              }
            </button>
          ))}
          {data && (
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
              v{data.version} · {data.updatedAt
                ? new Date(data.updatedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
                : ""}
              {data.lastUpdatedBy && ` · ${data.lastUpdatedBy}`}
            </span>
          )}
        </div>
        <ActionButton saving={saving} onClick={handleSave} label="Save Document" />
      </div>

      <StyledInput
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Document title…"
        style={{ fontSize: 15, fontWeight: 700, fontFamily: "var(--font-display)", marginBottom: 12 }}
      />

      {mode === "edit"
        ? <RichEditor value={delta || content} onChange={handleChange} editorRef={editorRef} />
        : <Preview html={content} />
      }
    </div>
  );
}

/* ══════════════════════════════════════
   FAQ SECTION
══════════════════════════════════════ */
const ITEMS_PER_PAGE = 10;

function FaqSection() {
  const [languages,    setLanguages]    = useState([]);
  const [selLang,      setSelLang]      = useState(null);
  const [langLoading,  setLangLoading]  = useState(true);

  const [categories,    setCategories]  = useState([]);
  const [items,         setItems]       = useState([]);
  const [selCat,        setSelCat]      = useState(null);
  const [loading,       setLoading]     = useState(false);
  const [itemPage,      setItemPage]    = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const [catForm,    setCatForm]    = useState({ key: "", title: "" });
  const [catSaving,  setCatSaving]  = useState(false);
  const [itemForm,   setItemForm]   = useState({ question: "", answer: "" });
  const [itemSaving, setItemSaving] = useState(false);

  useEffect(() => {
    faqApi.getLanguages()
      .then(r => {
        const langs = r.data.data || [];
        setLanguages(langs);
        if (langs.length > 0) {
          const english = langs.find(l =>
            (l.name         || "").toLowerCase() === "english" ||
            (l.languageName || "").toLowerCase() === "english" ||
            (l.code         || "").toLowerCase() === "en"      ||
            (l.code         || "").toLowerCase() === "eng"
          );
          setSelLang((english ?? langs[0]).id);
        }
      })
      .catch(() => showToast("Failed to load languages", "error"))
      .finally(() => setLangLoading(false));
  }, []);

  useEffect(() => {
    if (!selLang) return;
    setLoading(true);
    setCategories([]);
    setItems([]);
    setSelCat(null);

    faqApi.getCategoriesByLanguage(selLang)
      .then(r => {
        const grouped = r.data.data || [];
        const cats    = grouped.map(g => g.category || g.faqCategory || g);
        const allItems = grouped.flatMap(g => g.items || g.faqItems || []);
        setCategories(cats);
        setItems(allItems);
        if (cats.length > 0) setSelCat(cats[0].id);
      })
      .catch(() => showToast("Failed to load FAQ data", "error"))
      .finally(() => setLoading(false));
  }, [selLang]);

  useEffect(() => { setItemPage(0); }, [selCat]);

  const catItems   = items.filter(i => i.categoryId === selCat);
  const totalPages = Math.ceil(catItems.length / ITEMS_PER_PAGE);
  const canAddCategory = !!catForm.title.trim();
  const pagedItems = catItems.slice(itemPage * ITEMS_PER_PAGE, (itemPage + 1) * ITEMS_PER_PAGE);

  /* ── Category actions ── */
  const saveCategory = async () => {
    if (!catForm.title.trim() || !selLang) return;
    setCatSaving(true);
    try {
      const res = await faqApi.createCategory({
        ...catForm,
        key: catForm.key || catForm.title.toLowerCase().replace(/\s+/g, "_"),
        sortOrder: categories.length + 1,
        languageId: selLang,
      });
      const newCat = res.data.data;
      setCategories(prev => [...prev, newCat]);
      setSelCat(newCat.id);
      setCatForm({ key: "", title: "" });
      showToast("Category created");
    } catch { showToast("Failed to create category", "error"); }
    setCatSaving(false);
  };

  const deleteCategory = (cat) => {
    setConfirmDialog({
      message: `Soft-delete "${cat.title}"?`,
      subText: "All FAQ items in this category will also be hidden. This can be reversed from the database.",
      onConfirm: async () => {
        try {
          await faqApi.deleteCategory(cat.id);
          const remaining = categories.filter(c => c.id !== cat.id);
          setCategories(remaining);
          setItems(prev => prev.filter(i => i.categoryId !== cat.id));
          setSelCat(remaining[0]?.id ?? null);
          showToast(`"${cat.title}" deleted`, "success");
        } catch { showToast("Failed to delete category", "error"); }
      },
    });
  };

  /* ── Item actions ── */
  const saveItem = async () => {
    if (!itemForm.question.trim() || !selCat || !selLang) return;
    setItemSaving(true);
    try {
      const res  = await faqApi.createItem({
        ...itemForm,
        categoryId: selCat,
        sortOrder: catItems.length + 1,
        languageId: selLang,
      });
      const saved = res.data.data;
      if (saved?.id) setItems(prev => [...prev, saved]);
      setItemForm({ question: "", answer: "" });
      showToast("FAQ item added");
    } catch { showToast("Failed to add item", "error"); }
    setItemSaving(false);
  };

  const deleteItem = (item) => {
    setConfirmDialog({
      message: "Soft-delete this FAQ item?",
      subText: `"${item.question.slice(0, 90)}${item.question.length > 90 ? "…" : ""}" — This can be reversed from the database.`,
      onConfirm: async () => {
        try {
          await faqApi.deleteItem(item.id);
          const remainingInCat = catItems.length - 1;
          const maxPage = Math.max(0, Math.ceil(remainingInCat / ITEMS_PER_PAGE) - 1);
          if (itemPage > maxPage) setItemPage(maxPage);
          setItems(prev => prev.filter(i => i.id !== item.id));
          showToast("Item deleted · Reversible from the database", "success");
        } catch { showToast("Failed to delete item", "error"); }
      },
    });
  };

  // ── Language Selector ──
  const LanguageSelector = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.7px", display: "inline-flex", alignItems: "center", gap: 5 }}>
        <Globe size={13} strokeWidth={2} /> Language:
      </span>
      <div style={{ position: "relative" }}>
        <select
          value={selLang || ""}
          onChange={e => setSelLang(e.target.value)}
          disabled={langLoading}
          style={{
            padding: "7px 32px 7px 12px",
            fontSize: 13, fontWeight: 600,
            border: "1px solid var(--border)", borderRadius: 8,
            background: "var(--bg-card)", color: "var(--text-primary)",
            fontFamily: "var(--font-body)",
            cursor: langLoading ? "not-allowed" : "pointer",
            outline: "none", appearance: "none", WebkitAppearance: "none",
            minWidth: 140, boxShadow: "var(--shadow-sm)",
          }}
          onFocus={e  => e.target.style.borderColor = "#0F3D3E"}
          onBlur={e   => e.target.style.borderColor = "var(--border)"}
        >
          {langLoading && <option value="">Loading…</option>}
          {languages.map(lang => (
            <option key={lang.id} value={lang.id}>
              {lang.name || lang.languageName || lang.code || lang.id}
            </option>
          ))}
        </select>
        <ChevronDown
          size={12} strokeWidth={2.5} color="#0F3D3E"
          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
        />
      </div>

      {selLang && !langLoading && (
        <span style={{
          fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20,
          background: "rgba(15,61,62,0.08)", color: "#0F3D3E",
          border: "1px solid rgba(15,61,62,0.18)",
        }}>
          {categories.length} categories · {items.length} items
        </span>
      )}
    </div>
  );

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <ConfirmDialog dialog={confirmDialog} onClose={() => setConfirmDialog(null)} />

      {/* Top bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          {loading
            ? "Loading FAQ data…"
            : `${categories.length} categor${categories.length === 1 ? "y" : "ies"} found`}
        </div>
        <LanguageSelector />
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
          <div style={{ width: 32, height: 32, margin: "0 auto 10px", border: "3px solid var(--border)", borderTop: "3px solid #0F3D3E", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
          Loading FAQ for selected language…
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 20, alignItems: "start" }}>

          {/* Left: Categories */}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--shadow-sm)" }}>
            <SectionHeader title={`Categories (${categories.length})`} />
            <div style={{ padding: 10 }}>
              {categories.length === 0 && (
                <div style={{ padding: "20px 12px", fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>
                  No categories for this language.
                </div>
              )}

              {categories.map(cat => (
                <div
                  key={cat.id}
                  onClick={() => setSelCat(cat.id)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "9px 12px", borderRadius: "var(--radius)", cursor: "pointer", marginBottom: 3,
                    background: selCat === cat.id ? "#0F3D3E" : "transparent",
                    color:      selCat === cat.id ? "#fff"    : "var(--text-secondary)",
                    fontSize: 13, fontWeight: selCat === cat.id ? 600 : 400,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { if (selCat !== cat.id) e.currentTarget.style.background = "rgba(15,61,62,0.07)"; }}
                  onMouseLeave={e => { if (selCat !== cat.id) e.currentTarget.style.background = "transparent"; }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {cat.title}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10, marginLeft: 6, flexShrink: 0,
                    background: selCat === cat.id ? "rgba(255,255,255,0.2)" : "rgba(15,61,62,0.08)",
                    color:      selCat === cat.id ? "#fff" : "#0F3D3E",
                  }}>
                    {items.filter(i => i.categoryId === cat.id).length}
                  </span>
                  <button
                    onClick={e => { e.stopPropagation(); deleteCategory(cat); }}
                    title="Soft-delete this category"
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: selCat === cat.id ? "rgba(255,255,255,0.7)" : "#dc2626",
                      opacity: 0.65, padding: "2px 4px", lineHeight: 1, flexShrink: 0, marginLeft: 4,
                      display: "flex", alignItems: "center",
                    }}
                  >
                    <Trash2 size={13} strokeWidth={2} />
                  </button>
                </div>
              ))}

              {/* New category form */}
              <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: "1px dashed var(--border)", background: "rgba(15,61,62,0.02)" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  New Category
                </div>
                <StyledInput
                  placeholder="Title"
                  value={catForm.title}
                  onChange={e => setCatForm(p => ({ ...p, title: e.target.value }))}
                  style={{ marginBottom: 6 }}
                />
                <StyledInput
                  placeholder="Key (auto if blank)"
                  value={catForm.key}
                  onChange={e => setCatForm(p => ({ ...p, key: e.target.value }))}
                  style={{ marginBottom: 8 }}
                />
                <ActionButton
                  saving={catSaving}
                  onClick={saveCategory}
                  label="Add Category"
                  small
                  disabled={!canAddCategory}
                />
              </div>
            </div>
          </div>

          {/* Right: Items */}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--shadow-sm)" }}>
            {!selCat ? (
              <div style={{ padding: 60, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                Select a category to view its FAQ items
              </div>
            ) : (
              <>
                <SectionHeader title={`FAQ Items · ${categories.find(c => c.id === selCat)?.title ?? ""} (${catItems.length})`} />
                <div style={{ padding: 16 }}>
                  {catItems.length === 0 ? (
                    <div style={{ color: "var(--text-muted)", fontSize: 13, padding: "20px 0 10px" }}>
                      No items yet. Add one below.
                    </div>
                  ) : (
                    <>
                      {pagedItems.map(item => (
                        <div key={item.id} style={{
                          background: "rgba(15,61,62,0.02)", border: "1px solid var(--border)",
                          borderRadius: 10, padding: "14px 16px", marginBottom: 10, position: "relative",
                        }}>
                          <div style={{ paddingRight: 32 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)", marginBottom: 5 }}>
                              Q: {item.question}
                            </div>
                            <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
                              A: {item.answer}
                            </div>
                          </div>
                          <button
                            onClick={() => deleteItem(item)}
                            title="Soft-delete this FAQ item"
                            style={{
                              position: "absolute", top: 12, right: 12,
                              background: "none", border: "none", cursor: "pointer",
                              color: "#dc2626", opacity: 0.55, lineHeight: 1,
                              display: "flex", alignItems: "center",
                            }}
                          >
                            <Trash2 size={14} strokeWidth={2} />
                          </button>
                        </div>
                      ))}

                      {totalPages > 1 && (
                        <div style={{ marginTop: 4, marginBottom: 8 }}>
                          <Pagination page={itemPage} totalPages={totalPages} onChange={setItemPage} />
                        </div>
                      )}
                    </>
                  )}

                  {/* Add item form */}
                  <div style={{ marginTop: 16, padding: 16, borderRadius: 10, border: "1px dashed var(--border)", background: "rgba(15,61,62,0.02)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.8px" }}>
                      Add New Item
                    </div>
                    <StyledInput
                      placeholder="Question"
                      value={itemForm.question}
                      onChange={e => setItemForm(p => ({ ...p, question: e.target.value }))}
                      style={{ marginBottom: 8 }}
                    />
                    <textarea
                      value={itemForm.answer}
                      onChange={e => setItemForm(p => ({ ...p, answer: e.target.value }))}
                      placeholder="Answer…"
                      rows={3}
                      style={{
                        width: "100%", padding: "9px 12px", fontSize: 13,
                        border: "1px solid var(--border)", borderRadius: 8, outline: "none",
                        fontFamily: "var(--font-body)", resize: "vertical",
                        color: "var(--text-primary)", background: "var(--bg-card)",
                        boxSizing: "border-box", marginBottom: 10,
                      }}
                      onFocus={e => e.target.style.borderColor = "#0F3D3E"}
                      onBlur={e  => e.target.style.borderColor = "var(--border)"}
                    />
                    <ActionButton saving={itemSaving} onClick={saveItem} label="Add Item" small />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ══════════════════════════════════════
   ROUTE META
══════════════════════════════════════ */
const ROUTE_META = {
  "/legal/terms-and-conditions": { title: "Terms & Conditions", subtitle: "Edit the Terms & Conditions displayed in the app" },
  "/legal/privacy-policy":       { title: "Privacy Policy",     subtitle: "Edit the Privacy Policy displayed in the app"     },
  "/legal/faq":                  { title: "FAQ",                subtitle: "Manage FAQ categories and items shown in the app"  },
};

/* ══════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════ */
export default function LegalContentPage() {
  const location = useLocation();
  const meta     = ROUTE_META[location.pathname] ?? ROUTE_META["/legal/terms-and-conditions"];
  const isFaq    = location.pathname === "/legal/faq";

  return (
    <div>
      <PageHeader title={meta.title} subtitle={meta.subtitle} />
      {isFaq ? <FaqSection /> : <DocEditor />}
    </div>
  );
}